import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Upload,
    Download,
    Calendar,
    TrendingUp,
    Package,
    AlertCircle,
    CheckCircle,
    XCircle,
    ClipboardList,
    Edit2,
    Save,
    X as XIcon,
    ChevronLeft,
    ChevronRight,
    FileSpreadsheet,
    BarChart3,
    PieChart,
    Activity,
    Trash2,
} from "lucide-react";
import { db } from "../../firebase";
import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    query,
    where,
    Timestamp,
    onSnapshot,
    deleteDoc,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
} from "recharts";

// Nombres de sucursales según especificación
const SUCURSALES = [
    "OLIVARES",
    "REFORMA",
    "EXPRESS",
    "MARIACHI",
    "VERBENA",
    "VILDOSOLA",
    "LIBERTAD",
    "GUEVARA",
    "NVO. HILLO",
    "QUIROGA",
    "PROGRESO",
    "CARRETA",
];

// Días de la semana en español
const DIAS_SEMANA = [
    "DOMINGO",
    "LUNES",
    "MARTES",
    "MIÉRCOLES",
    "JUEVES",
    "VIERNES",
    "SÁBADO",
];

// Meses en español para el selector
const MESES = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
];

// Colores para los gráficos
const COLORS = [
    "#FF0000",
    "#CC0000",
    "#FF6B6B",
    "#FFA500",
    "#FFD700",
    "#4CAF50",
    "#2196F3",
    "#9C27B0",
    "#E91E63",
    "#00BCD4",
    "#8BC34A",
    "#FF5722",
];

const InventarioSucursales = () => {
    // Estados principales
    const [inventarioData, setInventarioData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [notification, setNotification] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // Estados para el input de texto CSV
    const [showTextInput, setShowTextInput] = useState(false);
    const [csvText, setCsvText] = useState("");

    // Estados para borrar mes
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);

    // Estados para el selector de mes
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

    // Estados para edición
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");

    // Estados para estadísticas
    const [stats, setStats] = useState({
        totalSalidas: 0,
        promedioSalidas: 0,
        totalDevoluciones: 0,
        mejorSucursal: "",
        peorSucursal: "",
    });

    // Ref para el input de archivo
    const fileInputRef = useRef(null);

    // Mostrar notificación
    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 5000);
    };

    // Convertir fecha CSV a Date object
    const parseCSVDate = (dateString) => {
        if (!dateString) return null;

        try {
            // Si es un timestamp Unix (número como 1735603200)
            if (typeof dateString === "number" || !isNaN(parseFloat(dateString))) {
                const timestamp = typeof dateString === "number" ? dateString : parseFloat(dateString);

                // Verificar si es timestamp en segundos o milisegundos
                // Si es menor que 10000000000, es en segundos (antes del año 2286)
                if (timestamp < 10000000000) {
                    // Timestamp en segundos, convertir a milisegundos
                    const date = new Date(timestamp * 1000);
                    if (!isNaN(date.getTime())) {
                        return date;
                    }
                } else {
                    // Timestamp en milisegundos
                    const date = new Date(timestamp);
                    if (!isNaN(date.getTime())) {
                        return date;
                    }
                }
            }

            // Si es formato ISO: "2025-02-01T00:00:00.000Z"
            if (typeof dateString === "string" && dateString.includes("T")) {
                const date = new Date(dateString);
                if (!isNaN(date.getTime())) {
                    return date;
                }
            }

            // Si es string como "1-Feb"
            const dateStr = dateString.toString().trim();
            const parts = dateStr.split("-");
            if (parts.length === 2) {
                const day = parseInt(parts[0]);
                const monthMap = {
                    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
                    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
                    Ene: 0, Feb: 1, Mar: 2, Abr: 3, May: 4, Jun: 5,
                    Jul: 6, Ago: 7, Sep: 8, Oct: 9, Nov: 10, Dic: 11,
                };
                const month = monthMap[parts[1]];
                if (month !== undefined) {
                    return new Date(selectedYear, month, day);
                }
            }
        } catch (error) {
            console.error("Error parseando fecha:", error);
        }

        return null;
    };

    // Limpiar y parsear números del CSV
    const parseNumber = (value) => {
        if (value === null || value === undefined || value === "") return 0;

        // Si ya es un número, retornarlo
        if (typeof value === "number") return value;

        // Si es string, limpiar comas y parsear
        const cleaned = value.toString().replace(/,/g, "").trim();
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
    };

    // Obtener día de la semana en español (CORREGIDO PARA UTC)
    const getDiaSemana = (date) => {
        // Usar getUTCDay() en lugar de getDay() para evitar problemas de zona horaria
        return DIAS_SEMANA[date.getUTCDay()];
    };

    // Formatear fecha a YYYY-MM-DD (CORREGIDO PARA UTC)
    const formatDateId = (date) => {
        // Usar métodos UTC para que la fecha sea consistente sin importar la zona horaria
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, "0");
        const day = String(date.getUTCDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Procesar archivo CSV
    const processCSVFile = (file) => {
        setLoading(true);
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
                    header: 1,
                    defval: "",
                });

                // Buscar la fila de encabezados
                let headerRowIndex = -1;
                for (let i = 0; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (
                        row.some(
                            (cell) =>
                                cell &&
                                cell.toString().toUpperCase().includes("FECHA")
                        )
                    ) {
                        headerRowIndex = i;
                        break;
                    }
                }

                if (headerRowIndex === -1) {
                    throw new Error(
                        "No se encontró la fila de encabezados con 'FECHA'"
                    );
                }

                const headers = jsonData[headerRowIndex].map((h) =>
                    h ? h.toString().trim() : ""
                );

                // Crear mapa de índices de columnas (case-insensitive)
                const getColumnIndex = (columnName) => {
                    return headers.findIndex(
                        (h) => h.toUpperCase() === columnName.toUpperCase()
                    );
                };

                // Procesar cada fila de datos
                const processedData = [];
                for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0 || !row[0]) continue;

                    const fecha = parseCSVDate(row[0]);
                    if (!fecha) {
                        console.warn(`Fila ${i + 1}: Fecha inválida`, row[0]);
                        continue;
                    }

                    const sucursales = {};
                    let totalSalidaCalculado = 0;

                    // Mapear sucursales - buscar en headers case-insensitive
                    SUCURSALES.forEach((sucursal) => {
                        const idx = headers.findIndex(
                            (h) => h.toUpperCase() === sucursal.toUpperCase()
                        );
                        if (idx !== -1) {
                            const valor = parseNumber(row[idx]);
                            sucursales[sucursal] = valor;
                            totalSalidaCalculado += valor;
                        }
                    });

                    // Buscar columnas con case-insensitive
                    const inicioIdx = getColumnIndex("INICIO");
                    const entradaIdx = getColumnIndex("entrada");
                    const salidaIdx = getColumnIndex("SALIDA");
                    const totalIdx = getColumnIndex("TOTAL");
                    const devolucionIdx = getColumnIndex("DEVOLUCION");

                    const rowData = {
                        fecha: Timestamp.fromDate(fecha),
                        fechaId: formatDateId(fecha),
                        diaSemana: getDiaSemana(fecha),
                        inicio: inicioIdx !== -1 ? parseNumber(row[inicioIdx]) : 0,
                        entrada: entradaIdx !== -1 ? parseNumber(row[entradaIdx]) : 0,
                        salidaTotal:
                            salidaIdx !== -1
                                ? parseNumber(row[salidaIdx])
                                : totalSalidaCalculado,
                        total: totalIdx !== -1 ? parseNumber(row[totalIdx]) : 0,
                        devolucion:
                            devolucionIdx !== -1 ? parseNumber(row[devolucionIdx]) : 0,
                        sucursales,
                        totalSalidaCalculado,
                        valid: true,
                    };

                    // Validar que la suma coincida (con tolerancia de 1 unidad)
                    if (
                        Math.abs(
                            rowData.salidaTotal - rowData.totalSalidaCalculado
                        ) > 1
                    ) {
                        rowData.valid = false;
                        rowData.warning = `Total de salida (${rowData.salidaTotal}) no coincide con suma de sucursales (${rowData.totalSalidaCalculado})`;
                    }

                    processedData.push(rowData);
                }

                if (processedData.length === 0) {
                    throw new Error("No se encontraron datos válidos en el archivo");
                }

                setPreviewData(processedData);
                setShowPreview(true);
                showNotification(
                    "success",
                    `${processedData.length} filas procesadas correctamente`
                );
            } catch (error) {
                console.error("Error procesando CSV:", error);
                showNotification(
                    "error",
                    `Error al procesar archivo: ${error.message}`
                );
            } finally {
                setLoading(false);
            }
        };

        reader.readAsArrayBuffer(file);
    };

    // Manejar carga de archivo
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const extension = file.name.split(".").pop().toLowerCase();
            if (extension === "csv") {
                processCSVFile(file);
            } else {
                showNotification(
                    "error",
                    "Por favor selecciona un archivo CSV (.csv)"
                );
            }
        }
    };

    // Procesar CSV desde texto pegado
    const processCSVText = () => {
        if (!csvText.trim()) {
            showNotification("warning", "Por favor pega el contenido CSV");
            return;
        }

        setLoading(true);

        try {
            // Convertir el texto a un formato que xlsx pueda leer
            const workbook = XLSX.read(csvText, { type: "string" });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
                header: 1,
                defval: "",
            });

            // Buscar la fila de encabezados
            let headerRowIndex = -1;
            for (let i = 0; i < jsonData.length; i++) {
                const row = jsonData[i];
                if (
                    row.some(
                        (cell) =>
                            cell &&
                            cell.toString().toUpperCase().includes("FECHA")
                    )
                ) {
                    headerRowIndex = i;
                    break;
                }
            }

            if (headerRowIndex === -1) {
                throw new Error(
                    "No se encontró la fila de encabezados con 'FECHA'"
                );
            }

            const headers = jsonData[headerRowIndex].map((h) =>
                h ? h.toString().trim() : ""
            );

            // Crear mapa de índices de columnas (case-insensitive)
            const getColumnIndex = (columnName) => {
                return headers.findIndex(
                    (h) => h.toUpperCase() === columnName.toUpperCase()
                );
            };

            // Procesar cada fila de datos
            const processedData = [];
            for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
                const row = jsonData[i];
                if (!row || row.length === 0 || !row[0]) continue;

                const fecha = parseCSVDate(row[0]);
                if (!fecha) {
                    console.warn(`Fila ${i + 1}: Fecha inválida`, row[0]);
                    continue;
                }

                const sucursales = {};
                let totalSalidaCalculado = 0;

                // Mapear sucursales - buscar en headers case-insensitive
                SUCURSALES.forEach((sucursal) => {
                    const idx = headers.findIndex(
                        (h) => h.toUpperCase() === sucursal.toUpperCase()
                    );
                    if (idx !== -1) {
                        const valor = parseNumber(row[idx]);
                        sucursales[sucursal] = valor;
                        totalSalidaCalculado += valor;
                    }
                });

                // Buscar columnas con case-insensitive
                const inicioIdx = getColumnIndex("INICIO");
                const entradaIdx = getColumnIndex("entrada");
                const salidaIdx = getColumnIndex("SALIDA");
                const totalIdx = getColumnIndex("TOTAL");
                const devolucionIdx = getColumnIndex("DEVOLUCION");

                const rowData = {
                    fecha: Timestamp.fromDate(fecha),
                    fechaId: formatDateId(fecha),
                    diaSemana: getDiaSemana(fecha),
                    inicio: inicioIdx !== -1 ? parseNumber(row[inicioIdx]) : 0,
                    entrada: entradaIdx !== -1 ? parseNumber(row[entradaIdx]) : 0,
                    salidaTotal:
                        salidaIdx !== -1
                            ? parseNumber(row[salidaIdx])
                            : totalSalidaCalculado,
                    total: totalIdx !== -1 ? parseNumber(row[totalIdx]) : 0,
                    devolucion:
                        devolucionIdx !== -1 ? parseNumber(row[devolucionIdx]) : 0,
                    sucursales,
                    totalSalidaCalculado,
                    valid: true,
                };

                // Validar que la suma coincida (con tolerancia de 1 unidad)
                if (
                    Math.abs(
                        rowData.salidaTotal - rowData.totalSalidaCalculado
                    ) > 1
                ) {
                    rowData.valid = false;
                    rowData.warning = `Total de salida (${rowData.salidaTotal}) no coincide con suma de sucursales (${rowData.totalSalidaCalculado})`;
                }

                processedData.push(rowData);
            }

            if (processedData.length === 0) {
                throw new Error("No se encontraron datos válidos en el texto CSV");
            }

            setPreviewData(processedData);
            setShowPreview(true);
            setShowTextInput(false);
            setCsvText("");
            showNotification(
                "success",
                `${processedData.length} filas procesadas correctamente`
            );
        } catch (error) {
            console.error("Error procesando CSV:", error);
            showNotification(
                "error",
                `Error al procesar CSV: ${error.message}`
            );
        } finally {
            setLoading(false);
        }
    };

    // Guardar datos en Firestore (mejorado para múltiples meses)
    const saveToFirestore = async () => {
        if (!previewData || previewData.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        let successCount = 0;
        let errorCount = 0;
        let updatedCount = 0;

        // Agrupar por mes para el resumen
        const monthStats = {};

        try {
            for (let i = 0; i < previewData.length; i++) {
                const item = previewData[i];
                try {
                    // Verificar si ya existe
                    const existingDoc = await getDoc(doc(db, "inventario", item.fechaId));
                    const isUpdate = existingDoc.exists();

                    // Guardar en colección inventario
                    await setDoc(doc(db, "inventario", item.fechaId), {
                        fecha: item.fecha,
                        diaSemana: item.diaSemana,
                        inicio: item.inicio,
                        entrada: item.entrada,
                        salidaTotal: item.salidaTotal,
                        total: item.total,
                        devolucion: item.devolucion,
                        sucursales: item.sucursales,
                        updatedAt: Timestamp.now(),
                    });

                    if (isUpdate) {
                        updatedCount++;
                    } else {
                        successCount++;
                    }

                    // Estadísticas por mes
                    const date = item.fecha.toDate();
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                    const monthName = `${MESES[date.getMonth()]} ${date.getFullYear()}`;

                    if (!monthStats[monthKey]) {
                        monthStats[monthKey] = {
                            name: monthName,
                            count: 0,
                            updated: 0,
                        };
                    }

                    if (isUpdate) {
                        monthStats[monthKey].updated++;
                    } else {
                        monthStats[monthKey].count++;
                    }

                } catch (error) {
                    console.error(`Error guardando ${item.fechaId}:`, error);
                    errorCount++;
                }
                setUploadProgress(((i + 1) / previewData.length) * 100);
            }

            // Crear reportes mensuales automáticos
            await createMonthlyReport(previewData);

            // Construir mensaje de éxito con desglose por mes
            const monthSummary = Object.values(monthStats)
                .map(m => `${m.name}: ${m.count} nuevos${m.updated > 0 ? ` + ${m.updated} actualizados` : ''}`)
                .join(', ');

            const totalMeses = Object.keys(monthStats).length;

            showNotification(
                "success",
                `✅ ${successCount + updatedCount} registros procesados (${successCount} nuevos, ${updatedCount} actualizados) en ${totalMeses} ${totalMeses === 1 ? 'mes' : 'meses'}. ${monthSummary}${errorCount > 0 ? ` | ⚠️ ${errorCount} errores` : ""}`
            );
            setShowPreview(false);
            setPreviewData(null);
            fetchInventarioData();
        } catch (error) {
            console.error("Error guardando datos:", error);
            showNotification("error", "Error al guardar los datos");
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Crear reporte mensual automático
    const createMonthlyReport = async (data) => {
        if (!data || data.length === 0) return;

        // Agrupar por mes
        const monthGroups = {};
        data.forEach((item) => {
            const date = item.fecha.toDate();
            const monthKey = `${date.getFullYear()}-${String(
                date.getMonth() + 1
            ).padStart(2, "0")}`;

            if (!monthGroups[monthKey]) {
                monthGroups[monthKey] = [];
            }
            monthGroups[monthKey].push(item);
        });

        // Crear reporte para cada mes
        for (const [monthKey, items] of Object.entries(monthGroups)) {
            const totalSalidas = items.reduce((sum, i) => sum + i.salidaTotal, 0);
            const totalDevoluciones = items.reduce(
                (sum, i) => sum + i.devolucion,
                0
            );
            const promedioSalidas = totalSalidas / items.length;

            // Calcular totales por sucursal
            const sucursalesTotals = {};
            SUCURSALES.forEach((suc) => {
                sucursalesTotals[suc] = items.reduce(
                    (sum, i) => sum + (i.sucursales[suc] || 0),
                    0
                );
            });

            await setDoc(doc(db, "reportes_mensuales", monthKey), {
                mes: monthKey,
                totalDias: items.length,
                totalSalidas,
                totalDevoluciones,
                promedioSalidas: Math.round(promedioSalidas),
                sucursalesTotals,
                createdAt: Timestamp.now(),
            });
        }
    };

    // Borrar mes completo con confirmación
    const deleteMonth = async () => {
        const expectedText = `BORRAR ${MESES[selectedMonth].toUpperCase()} ${selectedYear}`;

        if (deleteConfirmText !== expectedText) {
            showNotification("error", `Debes escribir exactamente: ${expectedText}`);
            return;
        }

        setDeleting(true);
        setUploadProgress(0);
        let deletedCount = 0;
        let errorCount = 0;

        try {
            // Obtener todos los registros del mes
            const start = Timestamp.fromDate(
                new Date(selectedYear, selectedMonth, 1)
            );
            const end = Timestamp.fromDate(
                new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59)
            );

            const q = query(
                collection(db, "inventario"),
                where("fecha", ">=", start),
                where("fecha", "<=", end)
            );

            const snapshot = await getDocs(q);
            const total = snapshot.size;

            if (total === 0) {
                showNotification("info", "No hay registros para borrar en este mes");
                setShowDeleteConfirm(false);
                setDeleteConfirmText("");
                setDeleting(false);
                return;
            }

            // Borrar cada documento
            let progress = 0;
            for (const docSnapshot of snapshot.docs) {
                try {
                    await deleteDoc(doc(db, "inventario", docSnapshot.id));
                    deletedCount++;
                } catch (error) {
                    console.error(`Error borrando ${docSnapshot.id}:`, error);
                    errorCount++;
                }
                progress++;
                setUploadProgress((progress / total) * 100);
            }

            // Borrar reporte mensual
            const monthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
            try {
                await deleteDoc(doc(db, "reportes_mensuales", monthKey));
            } catch (error) {
                console.log("Reporte mensual no existía o error al borrar:", error);
            }

            showNotification(
                "success",
                `🗑️ ${deletedCount} registros eliminados de ${MESES[selectedMonth]} ${selectedYear}${errorCount > 0 ? ` | ⚠️ ${errorCount} errores` : ""}`
            );

            setShowDeleteConfirm(false);
            setDeleteConfirmText("");
            fetchInventarioData();
        } catch (error) {
            console.error("Error borrando mes:", error);
            showNotification("error", "Error al borrar el mes");
        } finally {
            setDeleting(false);
            setUploadProgress(0);
        }
    };

    // Obtener datos de Firestore para el mes seleccionado
    const fetchInventarioData = useCallback(async () => {
        setLoading(true);
        try {
            const start = Timestamp.fromDate(
                new Date(selectedYear, selectedMonth, 1)
            );
            const end = Timestamp.fromDate(
                new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59)
            );

            const q = query(
                collection(db, "inventario"),
                where("fecha", ">=", start),
                where("fecha", "<=", end)
            );

            // Usar onSnapshot para actualizaciones en tiempo real
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const data = [];
                snapshot.forEach((doc) => {
                    data.push({ id: doc.id, ...doc.data() });
                });

                // Ordenar por fecha
                data.sort((a, b) => a.fecha.toMillis() - b.fecha.toMillis());

                setInventarioData(data);
                setFilteredData(data);
                calculateStats(data);
                setLoading(false);
            });

            return unsubscribe;
        } catch (error) {
            console.error("Error fetching inventario:", error);
            showNotification("error", "Error al cargar los datos");
            setLoading(false);
        }
    }, [selectedYear, selectedMonth]);

    // Calcular estadísticas
    const calculateStats = (data) => {
        if (!data || data.length === 0) {
            setStats({
                totalSalidas: 0,
                promedioSalidas: 0,
                totalDevoluciones: 0,
                mejorSucursal: "",
                peorSucursal: "",
            });
            return;
        }

        const totalSalidas = data.reduce((sum, item) => sum + item.salidaTotal, 0);
        const totalDevoluciones = data.reduce(
            (sum, item) => sum + item.devolucion,
            0
        );
        const promedioSalidas = totalSalidas / data.length;

        // Calcular mejor y peor sucursal
        const sucursalsTotals = {};
        SUCURSALES.forEach((suc) => {
            sucursalsTotals[suc] = data.reduce(
                (sum, item) => sum + (item.sucursales?.[suc] || 0),
                0
            );
        });

        const sortedSucursales = Object.entries(sucursalsTotals).sort(
            (a, b) => b[1] - a[1]
        );

        setStats({
            totalSalidas: Math.round(totalSalidas),
            promedioSalidas: Math.round(promedioSalidas),
            totalDevoluciones: Math.round(totalDevoluciones),
            mejorSucursal: sortedSucursales[0]?.[0] || "",
            mejorSucursalTotal: sortedSucursales[0]?.[1] || 0,
            peorSucursal:
                sortedSucursales[sortedSucursales.length - 1]?.[0] || "",
            peorSucursalTotal:
                sortedSucursales[sortedSucursales.length - 1]?.[1] || 0,
        });
    };

    // Actualizar celda
    const updateCell = async (rowId, field, value) => {
        try {
            const numValue = parseFloat(value) || 0;

            if (field.startsWith("sucursales.")) {
                const sucursal = field.split(".")[1];
                const docRef = doc(db, "inventario", rowId);
                await updateDoc(docRef, {
                    [`sucursales.${sucursal}`]: numValue,
                    updatedAt: Timestamp.now(),
                });
            } else {
                await updateDoc(doc(db, "inventario", rowId), {
                    [field]: numValue,
                    updatedAt: Timestamp.now(),
                });
            }

            showNotification("success", "Actualizado correctamente");
        } catch (error) {
            console.error("Error actualizando:", error);
            showNotification("error", "Error al actualizar");
        }
    };

    // Exportar a CSV
    const exportToExcel = () => {
        if (filteredData.length === 0) {
            showNotification("warning", "No hay datos para exportar");
            return;
        }

        try {
            // Preparar datos para CSV con formato ISO para fechas
            const csvData = filteredData.map((item) => {
                const fecha = item.fecha.toDate();
                const row = {
                    FECHA: fecha.toISOString(),
                    INICIO: item.inicio,
                    entrada: item.entrada,
                    SALIDA: item.salidaTotal,
                    TOTAL: item.total,
                    DEVOLUCION: item.devolucion,
                };

                SUCURSALES.forEach((suc) => {
                    row[suc] = item.sucursales?.[suc] || "";
                });

                // Calcular total de salida
                const totalSalida = SUCURSALES.reduce(
                    (sum, suc) => sum + (item.sucursales?.[suc] || 0),
                    0
                );
                row["TOTAL DE SALIDA"] = totalSalida;

                return row;
            });

            // Crear workbook y exportar como CSV
            const ws = XLSX.utils.json_to_sheet(csvData);
            const csv = XLSX.utils.sheet_to_csv(ws);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

            const fileName = `Inventario_${MESES[selectedMonth]}_${selectedYear}.csv`;
            saveAs(blob, fileName);

            showNotification("success", `Archivo ${fileName} descargado`);
        } catch (error) {
            console.error("Error exportando:", error);
            showNotification("error", "Error al exportar archivo");
        }
    };

    // Cargar datos cuando cambia el mes
    useEffect(() => {
        const unsubscribe = fetchInventarioData();
        return () => {
            if (typeof unsubscribe === "function") {
                unsubscribe();
            }
        };
    }, [fetchInventarioData]);

    // Preparar datos para gráficos
    const prepareChartData = () => {
        if (!filteredData || filteredData.length === 0) return { lineData: [], barData: [], areaData: [] };

        // Datos para gráfico de líneas (ventas por día)
        const lineData = filteredData.map((item) => ({
            fecha: item.fecha.toDate().getDate(),
            salidas: item.salidaTotal,
            devoluciones: item.devolucion,
        }));

        // Datos para gráfico de barras (top sucursales)
        const sucursalsTotals = {};
        SUCURSALES.forEach((suc) => {
            sucursalsTotals[suc] = filteredData.reduce(
                (sum, item) => sum + (item.sucursales?.[suc] || 0),
                0
            );
        });

        const barData = Object.entries(sucursalsTotals)
            .sort((a, b) => b[1] - a[1])
            .map(([name, value]) => ({ name, value }));

        // Datos para gráfico de área (evolución de devoluciones)
        const areaData = filteredData.map((item) => ({
            fecha: item.fecha.toDate().getDate(),
            devoluciones: item.devolucion,
            salidas: item.salidaTotal,
        }));

        return { lineData, barData, areaData };
    };

    const { lineData, barData, areaData } = prepareChartData();

    return (
        <div className="space-y-6">
            {/* Notificación */}
            {notification && (
                <div
                    className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-6 py-4 rounded-lg shadow-lg animate-slide-in ${notification.type === "success"
                        ? "bg-green-50 border border-green-200"
                        : notification.type === "error"
                            ? "bg-red-50 border border-red-200"
                            : "bg-yellow-50 border border-yellow-200"
                        }`}
                >
                    {notification.type === "success" ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : notification.type === "error" ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                    ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                    )}
                    <p
                        className={`text-sm font-medium ${notification.type === "success"
                            ? "text-green-800"
                            : notification.type === "error"
                                ? "text-red-800"
                                : "text-yellow-800"
                            }`}
                    >
                        {notification.message}
                    </p>
                </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-red-50 rounded-xl">
                            <ClipboardList className="w-8 h-8 text-[#FF0000]" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">
                                Inventario de Sucursales
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Gestión y análisis de inventario diario por sucursal
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-2 px-4 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors"
                        >
                            <Upload className="w-4 h-4" />
                            Importar CSV
                        </button>
                        <button
                            onClick={() => setShowTextInput(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                            Pegar CSV como Texto
                        </button>
                        <button
                            onClick={exportToExcel}
                            disabled={filteredData.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className="w-4 h-4" />
                            Exportar CSV
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={filteredData.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Trash2 className="w-4 h-4" />
                            Borrar Mes
                        </button>
                    </div>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                />
            </div>

            {/* Selector de Mes */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Calendar className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            Seleccionar Período
                        </h3>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => {
                                if (selectedMonth === 0) {
                                    setSelectedMonth(11);
                                    setSelectedYear(selectedYear - 1);
                                } else {
                                    setSelectedMonth(selectedMonth - 1);
                                }
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div className="flex gap-2">
                            <select
                                value={selectedMonth}
                                onChange={(e) =>
                                    setSelectedMonth(parseInt(e.target.value))
                                }
                                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                            >
                                {MESES.map((mes, idx) => (
                                    <option key={idx} value={idx}>
                                        {mes}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedYear}
                                onChange={(e) =>
                                    setSelectedYear(parseInt(e.target.value))
                                }
                                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                            >
                                {Array.from({ length: 5 }, (_, i) => {
                                    const year = new Date().getFullYear() - 2 + i;
                                    return (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <button
                            onClick={() => {
                                if (selectedMonth === 11) {
                                    setSelectedMonth(0);
                                    setSelectedYear(selectedYear + 1);
                                } else {
                                    setSelectedMonth(selectedMonth + 1);
                                }
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Total Salidas</p>
                            <h3 className="text-2xl font-bold text-gray-900 mt-1">
                                {stats.totalSalidas.toLocaleString()}
                            </h3>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                            <TrendingUp className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Promedio Diario</p>
                            <h3 className="text-2xl font-bold text-gray-900 mt-1">
                                {stats.promedioSalidas.toLocaleString()}
                            </h3>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                            <Activity className="w-6 h-6 text-purple-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Total Devoluciones</p>
                            <h3 className="text-2xl font-bold text-gray-900 mt-1">
                                {stats.totalDevoluciones.toLocaleString()}
                            </h3>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg">
                            <XCircle className="w-6 h-6 text-red-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">Mejor Sucursal</p>
                            <h3 className="text-lg font-bold text-gray-900 mt-1">
                                {stats.mejorSucursal || "N/A"}
                            </h3>
                            <p className="text-xs text-gray-500">
                                {stats.mejorSucursalTotal?.toLocaleString() || 0}{" "}
                                unidades
                            </p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                            <Package className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Gráficos */}
            {lineData.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Gráfico de Líneas - Ventas por Día */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Ventas por Día del Mes
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={lineData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="fecha"
                                    label={{ value: "Día", position: "insideBottom", offset: -5 }}
                                />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="salidas"
                                    stroke="#FF0000"
                                    strokeWidth={2}
                                    name="Salidas"
                                />
                                <Line
                                    type="monotone"
                                    dataKey="devoluciones"
                                    stroke="#FFA500"
                                    strokeWidth={2}
                                    name="Devoluciones"
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Gráfico de Barras - Top Sucursales */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Top Sucursales por Volumen
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={barData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="value" name="Total">
                                    {barData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Gráfico de Área - Evolución */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 lg:col-span-2">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Evolución de Inventario y Devoluciones
                        </h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={areaData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="fecha"
                                    label={{ value: "Día", position: "insideBottom", offset: -5 }}
                                />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="salidas"
                                    stackId="1"
                                    stroke="#FF0000"
                                    fill="#FF0000"
                                    fillOpacity={0.6}
                                    name="Salidas"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="devoluciones"
                                    stackId="2"
                                    stroke="#FFA500"
                                    fill="#FFA500"
                                    fillOpacity={0.6}
                                    name="Devoluciones"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Tabla de Datos */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Registros de Inventario - {MESES[selectedMonth]} {selectedYear}
                        </h3>
                        <span className="text-sm text-gray-500">
                            {filteredData.length} registros
                        </span>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center p-12">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-600">Cargando datos...</p>
                        </div>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12">
                        <FileSpreadsheet className="w-16 h-16 text-gray-300 mb-4" />
                        <p className="text-gray-600 text-lg font-medium mb-2">
                            No hay datos para este período
                        </p>
                        <p className="text-gray-400 text-sm">
                            Importa un archivo CSV para comenzar
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Fecha
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        Día
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        Inicio
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        Entrada
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        Salida Total
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        Total
                                    </th>
                                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                        Devolución
                                    </th>
                                    {SUCURSALES.map((suc) => (
                                        <th
                                            key={suc}
                                            className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                                        >
                                            {suc}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredData.map((item) => {
                                    const fecha = item.fecha.toDate();
                                    const totalSucursales = SUCURSALES.reduce(
                                        (sum, suc) =>
                                            sum + (item.sucursales?.[suc] || 0),
                                        0
                                    );
                                    const hasWarning =
                                        Math.abs(item.salidaTotal - totalSucursales) >
                                        0.1;

                                    return (
                                        <tr
                                            key={item.id}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {fecha.getDate()}/{fecha.getMonth() + 1}/
                                                {fecha.getFullYear()}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                                {item.diaSemana}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {item.inicio}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {item.entrada}
                                            </td>
                                            <td
                                                className={`px-4 py-3 text-sm text-right font-medium ${hasWarning
                                                    ? "text-red-600"
                                                    : "text-gray-900"
                                                    }`}
                                            >
                                                {item.salidaTotal}
                                                {hasWarning && (
                                                    <AlertCircle className="w-4 h-4 inline ml-1" />
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {item.total}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                                {item.devolucion}
                                            </td>
                                            {SUCURSALES.map((suc) => (
                                                <td
                                                    key={suc}
                                                    className="px-4 py-3 text-sm text-gray-900 text-right cursor-pointer hover:bg-blue-50"
                                                    onClick={() => {
                                                        setEditingCell({
                                                            id: item.id,
                                                            field: `sucursales.${suc}`,
                                                        });
                                                        setEditValue(
                                                            item.sucursales?.[suc] || 0
                                                        );
                                                    }}
                                                >
                                                    {editingCell?.id === item.id &&
                                                        editingCell?.field ===
                                                        `sucursales.${suc}` ? (
                                                        <input
                                                            type="number"
                                                            value={editValue}
                                                            onChange={(e) =>
                                                                setEditValue(e.target.value)
                                                            }
                                                            onBlur={() => {
                                                                updateCell(
                                                                    item.id,
                                                                    `sucursales.${suc}`,
                                                                    editValue
                                                                );
                                                                setEditingCell(null);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    updateCell(
                                                                        item.id,
                                                                        `sucursales.${suc}`,
                                                                        editValue
                                                                    );
                                                                    setEditingCell(null);
                                                                }
                                                            }}
                                                            autoFocus
                                                            className="w-full px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        />
                                                    ) : (
                                                        item.sucursales?.[suc] || 0
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal para Pegar CSV como Texto */}
            {showTextInput && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Pegar Contenido CSV
                                    </h3>
                                    <p className="text-sm text-gray-600 mt-2">
                                        Copia y pega el contenido de tu archivo CSV aquí
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowTextInput(false);
                                        setCsvText("");
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <XIcon className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto p-6">
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Contenido CSV
                                </label>
                                <textarea
                                    value={csvText}
                                    onChange={(e) => setCsvText(e.target.value)}
                                    placeholder="fecha,INICIO,entrada,SALIDA,TOTAL,DEVOLUCION,OLIVARES,REFORMA,EXPRESS,MARIACHI,VERBENA,VILDOSOLA,LIBERTAD,GUEVARA,NVO. HILLO,QUIROGA,progreso,CARRETA,TOTAL DE SALIDA
1735603200,825,456,677,604,0,177,50,40,60,60,40,40,40,40,60,70,0,677
1735689600,604,330,570,364,,120,90,30,30,30,30,30,60,30,90,30,0,570
1735776000,364,,300,64,,30,90,0,,,,30,30,30,60,30,0,300
..."
                                    className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF0000] focus:border-transparent font-mono text-sm"
                                    style={{ resize: "vertical" }}
                                />
                                <p className="text-xs text-gray-500 mt-2">
                                    💡 Asegúrate de incluir los encabezados en la primera línea
                                </p>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                                    📋 Formato esperado:
                                </h4>
                                <ul className="text-xs text-blue-800 space-y-1">
                                    <li>
                                        • Fechas: <code className="bg-blue-100 px-1 rounded">Timestamp Unix (1735603200)</code> o ISO
                                    </li>
                                    <li>
                                        • Números con comas entre comillas: <code className="bg-blue-100 px-1 rounded">"2,369"</code>
                                    </li>
                                    <li>• Celdas vacías permitidas (se convertirán a 0)</li>
                                    <li>• <strong>NO incluyas columna dia_semana</strong> - se calcula automáticamente</li>
                                    <li>• Columnas separadas por comas</li>
                                    <li className="pt-2 border-t border-blue-200">
                                        • 🎉 <strong>PUEDES PEGAR MÚLTIPLES MESES A LA VEZ</strong> - El sistema los organizará automáticamente
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50">
                            {loading ? (
                                <div className="flex items-center justify-center">
                                    <div className="w-6 h-6 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin mr-3"></div>
                                    <span className="text-gray-700">Procesando CSV...</span>
                                </div>
                            ) : (
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setShowTextInput(false);
                                            setCsvText("");
                                        }}
                                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={processCSVText}
                                        disabled={!csvText.trim()}
                                        className="px-6 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Procesar CSV
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Vista Previa */}
            {showPreview && previewData && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">
                                    Vista Previa de Datos
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowPreview(false);
                                        setPreviewData(null);
                                    }}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <XIcon className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>
                            <div className="mt-3 space-y-2">
                                <p className="text-sm text-gray-600">
                                    {previewData.length} registros listos para importar
                                </p>
                                {(() => {
                                    // Agrupar por mes para mostrar resumen
                                    const monthGroups = {};
                                    previewData.forEach((item) => {
                                        const date = item.fecha.toDate();
                                        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
                                        const monthName = `${MESES[date.getMonth()]} ${date.getFullYear()}`;

                                        if (!monthGroups[monthKey]) {
                                            monthGroups[monthKey] = {
                                                name: monthName,
                                                count: 0,
                                            };
                                        }
                                        monthGroups[monthKey].count++;
                                    });

                                    const months = Object.values(monthGroups);
                                    if (months.length > 1) {
                                        return (
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-xs font-medium text-gray-500">
                                                    📅 Meses detectados:
                                                </span>
                                                {months.map((m, idx) => (
                                                    <span
                                                        key={idx}
                                                        className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium"
                                                    >
                                                        {m.name} ({m.count})
                                                    </span>
                                                ))}
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            {previewData.some((item) => !item.valid) && (
                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-yellow-800">
                                        <p className="font-medium">
                                            Advertencia: Algunos registros tienen
                                            inconsistencias
                                        </p>
                                        <p className="text-xs mt-1">
                                            Los totales de salida no coinciden con la suma
                                            de sucursales
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex-1 overflow-auto p-6">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Fecha
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                Día
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                                Salida Total
                                            </th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                                Suma Sucursales
                                            </th>
                                            <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                                Estado
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {previewData.map((item, idx) => (
                                            <tr
                                                key={idx}
                                                className={
                                                    item.valid
                                                        ? ""
                                                        : "bg-yellow-50"
                                                }
                                            >
                                                <td className="px-3 py-2 text-gray-900">
                                                    {item.fecha
                                                        .toDate()
                                                        .toLocaleDateString("es-MX")}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">
                                                    {item.diaSemana}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-900">
                                                    {item.salidaTotal}
                                                </td>
                                                <td className="px-3 py-2 text-right text-gray-900">
                                                    {item.totalSalidaCalculado}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    {item.valid ? (
                                                        <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                                                    ) : (
                                                        <AlertCircle className="w-4 h-4 text-yellow-600 mx-auto" />
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50">
                            {uploading ? (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-700">
                                            Guardando datos...
                                        </span>
                                        <span className="text-gray-900 font-medium">
                                            {Math.round(uploadProgress)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className="bg-[#FF0000] h-2.5 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setShowPreview(false);
                                            setPreviewData(null);
                                        }}
                                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={saveToFirestore}
                                        className="px-6 py-2 bg-[#FF0000] text-white rounded-lg hover:bg-[#CC0000] transition-colors"
                                    >
                                        Guardar {previewData.length} Registros
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmación para Borrar Mes */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden">
                        <div className="p-6 border-b border-gray-200 bg-red-50">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-red-100 rounded-full">
                                    <Trash2 className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-red-900">
                                        ⚠️ Borrar Mes Completo
                                    </h3>
                                    <p className="text-sm text-red-700 mt-1">
                                        Esta acción es irreversible
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-yellow-800">
                                        <p className="font-semibold mb-2">
                                            Estás a punto de borrar TODOS los registros de:
                                        </p>
                                        <p className="text-lg font-bold text-yellow-900">
                                            {MESES[selectedMonth]} {selectedYear}
                                        </p>
                                        <p className="mt-2">
                                            {filteredData.length} registros serán eliminados permanentemente.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Para confirmar, escribe exactamente:
                                </label>
                                <div className="mb-3 p-2 bg-gray-100 rounded text-center">
                                    <code className="text-sm font-mono font-bold">
                                        BORRAR {MESES[selectedMonth].toUpperCase()} {selectedYear}
                                    </code>
                                </div>
                                <input
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="Escribe aquí..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    disabled={deleting}
                                />
                            </div>

                            {deleting && (
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-700">Borrando registros...</span>
                                        <span className="text-gray-900 font-medium">
                                            {Math.round(uploadProgress)}%
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                        <div
                                            className="bg-red-600 h-2.5 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeleteConfirmText("");
                                }}
                                disabled={deleting}
                                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={deleteMonth}
                                disabled={deleting || deleteConfirmText !== `BORRAR ${MESES[selectedMonth].toUpperCase()} ${selectedYear}`}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {deleting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Borrando...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Borrar {filteredData.length} Registros
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventarioSucursales;

